import React, { useEffect, useMemo, useRef, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';

function getDirectAudioUrl(source) {
  const rawUrl = typeof source === 'string'
    ? source.trim()
    : source?.url?.trim() || source?.public_url?.trim() || '';

  if (!rawUrl) return '';

  try {
    const url = new URL(rawUrl);

    if (url.hostname === 'drive.google.com') {
      const fileMatch = url.pathname.match(/\/d\/([^/]+)/) ||
        url.searchParams.get('id')?.match(/(.+)/);

      if (fileMatch?.[1]) {
        return `https://drive.google.com/uc?export=download&id=${fileMatch[1]}`;
      }
    }

    if (url.hostname.endsWith('dropbox.com')) {
      url.searchParams.delete('dl');
      url.searchParams.set('raw', '1');
      return url.toString();
    }

    return url.toString();
  } catch {
    return rawUrl;
  }
}

export default function AudioPlayer({ src, title }) {
  const audioRef = useRef(null);
  const audioSrc = useMemo(() => getDirectAudioUrl(src), [src]);
  const [status, setStatus] = useState(audioSrc ? 'loading' : 'empty');
  const [error, setError] = useState('');

  useEffect(() => {
    setStatus(audioSrc ? 'loading' : 'empty');
    setError('');

    if (audioSrc && audioRef.current) {
      audioRef.current.load();
    }
  }, [audioSrc]);

  if (!audioSrc) {
    return (
      <div className="rounded-md border border-tan bg-cream p-4 text-sm text-muted">
        Audio is not available for this episode yet.
      </div>
    );
  }

  const handleError = () => {
    setStatus('error');
    setError(
      'This audio link could not be loaded. Confirm that it is a public, direct audio file URL.'
    );
  };

  return (
    <div className="rounded-md border border-tan bg-cream p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold text-dark-green">
        <SafeIcon name="Headphones" className="text-amber" />
        {title || 'Audio episode'}
      </div>

      <audio
        key={audioSrc}
        ref={audioRef}
        className="w-full"
        controls
        preload="metadata"
        onCanPlay={() => setStatus('ready')}
        onError={handleError}
      >
        <source src={audioSrc} />
        Your browser does not support audio playback.
      </audio>

      {status === 'loading' && (
        <p className="mt-3 text-xs text-muted">Loading audio...</p>
      )}

      {status === 'error' && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800">
          <p>{error}</p>
          <a
            href={audioSrc}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex font-bold underline"
          >
            Open the audio file directly
          </a>
        </div>
      )}
    </div>
  );
}